import { IndustryFill, IndustryHeaderStyle, IndustryShape } from "../Models/ChatbotTheme";
import { ColorUtils } from "./ColorUtils";

const SHAPE_RADIUS_MAP: Record<IndustryShape, string> = {
  pill: "999px",
  rounded: "20px",
  soft: "6px",
  square: "0px",
};

export const getShapeRadius = (shape?: string): string => {
  return SHAPE_RADIUS_MAP[(shape as IndustryShape) || "rounded"] || SHAPE_RADIUS_MAP.rounded;
};

const warnedColours = new Set<string>();

export const shade = (hex: string, amt: number): string => {
  const rgb = ColorUtils.hexToRgb(hex);
  if (!rgb) {
    // Development only — the widget runs inside the customer's own page, so a
    // malformed stored colour must not put anything in their visitors' console.
    if (process.env.NODE_ENV !== "production" && !warnedColours.has(hex)) {
      warnedColours.add(hex);
      console.warn(
        "[industry_style] could not parse colour, leaving it unshaded:",
        hex
      );
    }
    return hex;
  }
  const target = amt >= 1 ? 255 : 0;
  const ratio = amt >= 1 ? Math.min(1, amt - 1) : Math.min(1, 1 - amt);
  const lerp = (c: number) => Math.round(c + (target - c) * ratio);
  const toHex = (c: number) => c.toString(16).padStart(2, "0");
  return `#${toHex(lerp(rgb.r))}${toHex(lerp(rgb.g))}${toHex(lerp(rgb.b))}`;
};

export const getHeaderBackground = (
  headerStyle: IndustryHeaderStyle | string | undefined,
  accent: string
): string => {
  switch (headerStyle) {
    case "diagonal":
      return `linear-gradient(115deg, ${accent}, ${shade(accent, 0.78)})`;
    case "vivid":
      return `linear-gradient(120deg, ${shade(accent, 1.25)}, ${accent} 55%, ${shade(accent, 0.7)})`;
    case "dusk":
      return `linear-gradient(135deg, ${shade(accent, 0.82)}, #1c1f3a)`;
    case "soft_sweep":
      return `linear-gradient(100deg, ${accent} 0%, ${accent} 45%, ${shade(accent, 1.3)} 100%)`;
    case "solid":
    default:
      return accent;
  }
};

export interface IndustryButtonColors {
  background: string;
  border: string;
  color: string;
}

export const getIndustryButtonColors = (
  theme: any
): IndustryButtonColors | null => {
  const industry = theme?.industry_style;
  const accent = theme?.palette?.primary?.main;
  if (!industry?.enabled || !accent) return null;

  const fill: IndustryFill = industry.fill || "outline";
  switch (fill) {
    case "solid":
      return {
        background: accent,
        border: accent,
        color: ColorUtils.getTextColorForBackgroundHex(accent),
      };
    case "tinted":
      return {
        background: shade(accent, 1.75),
        border: accent,
        color: accent,
      };
    case "outline":
    default:
      return {
        background: "transparent",
        border: accent,
        color: accent,
      };
  }
};

